/**
 * Relationship-stage copy mapping — plan 291 M8-B (tasks 1393/1394, D8 rev
 * 4).
 *
 * Pure, side-effect-free. Not wired into any page/route yet — the buyer
 * profile-page surface that renders this is a later cycle. This file exists
 * so the BE↔FE contract (raw enum on the wire, display copy resolved
 * client-side) has a single home before that surface lands.
 *
 * Wire contract: the backend's `buyerConnectionDTO.relationship_stage`
 * (`GET /api/marketplace/connection?role=`) carries the RAW crm stage enum
 * only — never display copy (see
 * `backend/internal/api/marketplace_connections.go`'s `connectionToBuyerDTO`
 * and `backend/internal/services/crm/stage.go`'s `Stage` type, migration
 * 135). This module is the ONE place that enum maps to buyer-facing copy.
 *
 * D8 rev 4 buyer copy pin: stage `active` reads "Actively searching" (NOT
 * "Active" — that string would collide with the unrelated M6-B connection
 * `status === "active"` billing state, which is why the DTO fields are
 * named `status` vs `relationship_stage` distinctly on the wire in the
 * first place).
 */

/** The raw crm_contacts.stage enum values (migration 135). */
export type RelationshipStage =
  | "connected"
  | "agreement"
  | "active"
  | "under_contract"
  | "closed";

const RELATIONSHIP_STAGES: ReadonlySet<string> = new Set<RelationshipStage>([
  "connected",
  "agreement",
  "active",
  "under_contract",
  "closed",
]);

/**
 * Type guard: reports whether a raw wire value is a known relationship
 * stage. Unknown/future values fail closed to "not a known stage" rather
 * than being force-cast.
 */
export function isRelationshipStage(value: string): value is RelationshipStage {
  return RELATIONSHIP_STAGES.has(value);
}

/**
 * Canonical stage -> buyer-facing copy map (D8 rev 4 §9 design assets,
 * "Stage timeline (both lenses; 'Actively searching' copy)"). Every known
 * stage MUST have an entry here — enforced by a parity test
 * (`relationship-stage.test.ts`) that iterates every `RelationshipStage`
 * value.
 */
export const RELATIONSHIP_STAGE_LABELS: Readonly<
  Record<RelationshipStage, string>
> = {
  connected: "Connected",
  agreement: "Agreement in place",
  active: "Actively searching",
  under_contract: "Under contract",
  closed: "Closed",
};

/**
 * Resolves a raw wire stage value (or undefined, for a connection with no
 * `relationship_stage` on the wire — e.g. an unlinked/ended relationship
 * per the SILL-427 carve-out) to buyer-facing copy.
 *
 * Returns `undefined` for a missing/unknown value — callers render nothing
 * rather than a fabricated label. This mirrors the backend's own
 * fail-closed discipline: absence on the wire means "don't show a stage",
 * never a guessed default.
 */
export function relationshipStageLabel(
  stage: string | undefined | null,
): string | undefined {
  if (!stage) return undefined;
  return isRelationshipStage(stage)
    ? RELATIONSHIP_STAGE_LABELS[stage]
    : undefined;
}

// ── Stage timeline (plan 291 M8-B, task 1395 — relationship profile page) ────

/**
 * Canonical stage machine order (§4 stage machine, migration 135): the
 * relationship profile page's timeline (artboard 3b) renders exactly these
 * five nodes, in this order, always — this is the ONE ordering the timeline
 * component and its tests rely on.
 */
export const RELATIONSHIP_STAGE_ORDER: readonly RelationshipStage[] = [
  "connected",
  "agreement",
  "active",
  "under_contract",
  "closed",
];

export type StageNodeStatus = "done" | "now" | "upcoming";

/**
 * Classifies one timeline node relative to the relationship's current
 * stage: "done" (already passed), "now" (the current stage — highlighted),
 * or "upcoming" (not reached yet). An unknown/missing current stage fails
 * every node closed to "upcoming" rather than guessing a highlight.
 */
export function stageNodeStatus(
  current: string | undefined | null,
  node: RelationshipStage,
): StageNodeStatus {
  if (!current || !isRelationshipStage(current)) return "upcoming";
  const currentIdx = RELATIONSHIP_STAGE_ORDER.indexOf(current);
  const nodeIdx = RELATIONSHIP_STAGE_ORDER.indexOf(node);
  if (nodeIdx < currentIdx) return "done";
  if (nodeIdx === currentIdx) return "now";
  return "upcoming";
}
